import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const T = {
  teal: '#5EEAD4', tealDark: '#14B8A6',
  lavender: '#C4B5FD', lavenderDark: '#8B5CF6',
  coral: '#FDA4AF',
};

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <div
    className={`rounded-3xl border shadow-xl ${className}`}
    style={{
      background: 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
      borderColor: 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12)',
    }}
  >
    {children}
  </div>
);

const faqs = [
  {
    category: 'General',
    questions: [
      { q: 'What is Karvlo?', a: 'Karvlo is a ride-sharing platform built specifically for Hong Kong. We connect riders with verified drivers while keeping platform fees at just 5% — meaning drivers keep 95% of every fare.' },
      { q: 'How is Karvlo different from Uber or Grab?', a: 'Traditional platforms take 25-30% of each fare. Karvlo takes only 5%. We also eliminate surge pricing entirely, provide full fare transparency before booking, and are built natively for Hong Kong with bilingual support and Gaode Maps.' },
      { q: 'Is Karvlo available in my area?', a: 'Karvlo is currently available across all 18 districts of Hong Kong. We\'re expanding coverage daily as more drivers join the platform.' },
      { q: 'Is Karvlo in beta?', a: 'Yes! We\'re in open beta. All core features are live: booking, real-time tracking, payments, ratings, and messaging. Sign up today to get priority access.' },
    ],
  },
  {
    category: 'For Riders',
    questions: [
      { q: 'How do I book a ride?', a: 'Open the Karvlo app, enter your destination, see the fare upfront, and tap "Book". You\'ll be matched with the nearest driver in under 10 seconds on average.' },
      { q: 'Is there surge pricing?', a: 'Never. Karvlo does not have surge pricing. Your fare is calculated based on distance and time — that\'s it. What you see before booking is what you pay.' },
      { q: 'How do I pay?', a: 'We support credit/debit cards via Stripe. Octopus card integration is coming in Q2 2026. All payments are processed automatically after your ride.' },
      { q: 'What if I need to cancel?', a: 'Cancellations before driver assignment are always free. After assignment, there\'s a 2-minute grace period. After that, a small cancellation fee may apply based on the ride status.' },
      { q: 'Is it safe?', a: 'Yes. All drivers undergo background checks and identity verification. Every ride has real-time GPS tracking, an in-app emergency SOS button, and trip sharing with friends.' },
    ],
  },
  {
    category: 'For Drivers',
    questions: [
      { q: 'How much do drivers earn?', a: 'Drivers keep 95% of every fare — the highest take-home rate in the industry. For example, on a HK$100 fare, the driver keeps HK$95. We also offer performance bonuses and peak-hour multipliers.' },
      { q: 'How do I sign up as a driver?', a: 'Join our beta waitlist on the homepage. You\'ll need a valid HK driving license, vehicle registration, insurance, and to pass our background check.' },
      { q: 'How often do drivers get paid?', a: 'Weekly automatic payouts via bank transfer. You can track earnings in real-time through your driver dashboard.' },
      { q: 'Can I choose which rides to accept?', a: 'Yes. Drivers can see ride details (distance, estimated fare, pickup location) before accepting. There\'s no penalty for occasionally declining rides.' },
    ],
  },
  {
    category: 'Account & Support',
    questions: [
      { q: 'How do I contact support?', a: 'Email us at support@karvlo.com. During beta, our team typically responds within 15 minutes. We\'re building an in-app chat support feature for launch.' },
      { q: 'Can I use Karvlo in both English and Chinese?', a: 'Yes! The rider/driver app supports both English and Traditional Chinese (繁體中文). You can switch languages anytime in Settings.' },
      { q: 'How do I delete my account?', a: 'Go to Settings → Account → Delete Account, or email support@karvlo.com. We\'ll process your request within 24 hours and delete all personal data per our privacy policy.' },
    ],
  },
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('General');

  const toggle = (key: string) => setOpenIndex(openIndex === key ? null : key);

  const filteredFaqs = faqs.find(c => c.category === activeCategory)?.questions || [];

  return (
    <div className="relative z-10 pt-24">
      {/* Hero */}
      <section className="relative py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-sm font-bold tracking-[0.2em] uppercase mb-4" style={{ color: T.lavender }}>
              Help Center
            </p>
            <h1 className="text-5xl sm:text-6xl font-black text-slate-800 leading-tight mb-6">
              Frequently Asked
              <br />
              <span className="text-slate-400">Questions</span>
            </h1>
            <p className="text-xl text-slate-500 max-w-xl mx-auto">
              Everything you need to know about Karvlo.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category tabs */}
      <section className="relative px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {faqs.map(cat => (
              <button
                key={cat.category}
                onClick={() => { setActiveCategory(cat.category); setOpenIndex(null); }}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
                  activeCategory === cat.category
                    ? 'bg-slate-800 text-white shadow-lg'
                    : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                {cat.category}
              </button>
            ))}
          </div>

          {/* FAQ list */}
          <div className="space-y-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeCategory}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="space-y-3"
              >
                {filteredFaqs.map((faq, i) => {
                  const key = `${activeCategory}-${i}`;
                  const isOpen = openIndex === key;
                  return (
                    <GlassCard key={key} className="overflow-hidden">
                      <button
                        onClick={() => toggle(key)}
                        className="w-full flex items-center justify-between p-6 text-left"
                      >
                        <span className="font-semibold text-slate-800 pr-4">{faq.q}</span>
                        <motion.span
                          animate={{ rotate: isOpen ? 45 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="text-slate-400 text-xl flex-shrink-0"
                        >
                          +
                        </motion.span>
                      </button>
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <p className="px-6 pb-6 text-slate-500 leading-relaxed">{faq.a}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </GlassCard>
                  );
                })}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto text-center">
          <GlassCard className="p-10">
            <h3 className="text-2xl font-black text-slate-800 mb-3">Still have questions?</h3>
            <p className="text-slate-500 mb-6">Our team is here to help.</p>
            <a
              href="mailto:support@karvlo.com"
              className="inline-flex items-center justify-center px-8 py-3 bg-slate-800 rounded-2xl text-white font-bold shadow-lg hover:bg-slate-700 transition-all"
            >
              Contact Support
            </a>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
